
import { useMemo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import Prism from "prismjs";
import "prismjs/components/prism-json";
import "prismjs/components/prism-sql";
import type { CommentResponse } from "../../gen/models";

interface HighlightedMessageTextProps {
  msgId: string;
  content: string;
  comments: CommentResponse[];
  activeHighlightedCommentId: string | null;
  activeSelection?: {
    text: string;
    start?: number;
    end?: number;
  } | null;
  onSelectText?: (messageId: string) => void;
  onCommentClick: (commentId: string) => void;
}

function createRehypeSnippetHighlighter(
  activeText?: string | null,
  comments: CommentResponse[] = [],
  activeHighlightedCommentId: string | null = null
) {
  return () => (tree: any) => {
    const targets: Array<{
      text: string;
      type: "composer" | "comment";
      commentId?: string;
      isHighlighted?: boolean;
    }> = [];

    if (activeText && activeText.trim()) {
      targets.push({ text: activeText.trim(), type: "composer" });
    }

    for (const c of comments) {
      if (c.selected_text && c.selected_text.trim()) {
        targets.push({
          text: c.selected_text.trim(),
          type: "comment",
          commentId: c.id,
          isHighlighted: c.id === activeHighlightedCommentId,
        });
      }
    }

    if (targets.length === 0) return;

    function highlightInContainer(container: any) {
      const textNodes: Array<{ node: any; parent: any }> = [];
      function collect(node: any, parent: any) {
        if (node.type === "text" && node.value) {
          textNodes.push({ node, parent });
        } else if (node.children && node.tagName !== "pre") {
          for (const child of node.children) {
            collect(child, node);
          }
        }
      }
      collect(container, null);

      let containerText = "";
      const nodeOffsets: Array<{ node: any; parent: any; start: number; end: number }> = [];
      for (const item of textNodes) {
        const start = containerText.length;
        containerText += item.node.value;
        const end = containerText.length;
        nodeOffsets.push({ ...item, start, end });
      }

      for (const target of targets) {
        const fullClean = target.text.replace(/\s+/g, " ").trim();
        if (!fullClean) continue;

        // Split target by newlines in case selection spans multiple lines/paragraphs
        const subSnippets = target.text
          .split(/\r?\n/)
          .map((l) => l.replace(/\s+/g, " ").trim())
          .filter((l) => l.length > 0);

        for (const snippet of subSnippets) {
          let searchIdx = 0;
          const sLower = snippet.toLowerCase();
          const cLower = containerText.toLowerCase();

          while (searchIdx < cLower.length) {
            const found = cLower.indexOf(sLower, searchIdx);
            if (found === -1) break;

            const matchStart = found;
            const matchEnd = found + snippet.length;
            searchIdx = matchEnd;

            for (const item of nodeOffsets) {
              if (item.start >= matchEnd || item.end <= matchStart) continue;

              const node = item.node;
              const parent = item.parent;
              if (!parent || !parent.children) continue;

              const sliceStartInNode = Math.max(0, matchStart - item.start);
              const sliceEndInNode = Math.min(node.value.length, matchEnd - item.start);

              const before = node.value.slice(0, sliceStartInNode);
              const matchText = node.value.slice(sliceStartInNode, sliceEndInNode);
              const after = node.value.slice(sliceEndInNode);

              const markElement = {
                type: "element",
                tagName: "mark",
                properties: {
                  dataType: target.type,
                  dataCommentId: target.commentId,
                  dataHighlighted: target.isHighlighted ? "true" : "false",
                },
                children: [{ type: "text", value: matchText }],
              };

              const newChildren: any[] = [];
              if (before) newChildren.push({ type: "text", value: before });
              newChildren.push(markElement);
              if (after) newChildren.push({ type: "text", value: after });

              const idxInParent = parent.children.indexOf(node);
              if (idxInParent !== -1) {
                parent.children.splice(idxInParent, 1, ...newChildren);
              }
            }
          }
        }
      }
    }

    if (tree.children) {
      for (const child of tree.children) {
        highlightInContainer(child);
      }
    }
  };
}

export function HighlightedMessageText({
  msgId,
  content,
  comments,
  activeHighlightedCommentId,
  activeSelection,
  onSelectText,
  onCommentClick,
}: HighlightedMessageTextProps) {
  const rehypeSnippetPlugin = useMemo(
    () =>
      createRehypeSnippetHighlighter(
        activeSelection?.text,
        comments,
        activeHighlightedCommentId
      ),
    [activeSelection?.text, comments, activeHighlightedCommentId]
  );

  const markdownComponents: Components = useMemo(
    () => ({
      p: ({ children }) => <p className="leading-relaxed mb-2.5 last:mb-0">{children}</p>,
      strong: ({ children }) => <strong className="font-semibold text-inherit">{children}</strong>,
      em: ({ children }) => <em className="italic text-inherit">{children}</em>,
      ul: ({ children }) => <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>,
      ol: ({ children }) => <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>,
      li: ({ children }) => <li className="leading-relaxed">{children}</li>,
      h1: ({ children }) => <h1 className="text-lg font-bold mt-3.5 mb-1.5 text-inherit">{children}</h1>,
      h2: ({ children }) => <h2 className="text-base font-bold mt-3 mb-1 text-inherit">{children}</h2>,
      h3: ({ children }) => <h3 className="text-sm font-semibold mt-2.5 mb-1 text-inherit">{children}</h3>,
      h4: ({ children }) => <h4 className="text-xs font-semibold mt-2 mb-0.5 text-inherit uppercase tracking-wide">{children}</h4>,
      blockquote: ({ children }) => (
        <blockquote className="border-l-2 border-stone-400 dark:border-stone-600 pl-3 my-2 text-stone-600 dark:text-stone-400 italic">
          {children}
        </blockquote>
      ),
      a: ({ href, children }) => (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-amber-600 dark:text-amber-400 underline underline-offset-2 hover:opacity-80 break-words"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </a>
      ),
      hr: () => <hr className="my-3 border-stone-300 dark:border-stone-700" />,
      table: ({ children }) => (
        <div className="overflow-x-auto my-3">
          <table className="min-w-full text-xs text-left border-collapse border border-stone-300 dark:border-stone-700">
            {children}
          </table>
        </div>
      ),
      thead: ({ children }) => (
        <thead className="bg-stone-200/60 dark:bg-stone-800/80 font-medium text-inherit">
          {children}
        </thead>
      ),
      tbody: ({ children }) => <tbody>{children}</tbody>,
      tr: ({ children }) => (
        <tr className="border-b border-stone-200 dark:border-stone-800 last:border-b-0">
          {children}
        </tr>
      ),
      th: ({ children }) => (
        <th className="p-2 border border-stone-300 dark:border-stone-700 font-semibold text-inherit">
          {children}
        </th>
      ),
      td: ({ children }) => (
        <td className="p-2 border border-stone-300 dark:border-stone-700 text-inherit">
          {children}
        </td>
      ),
      pre: ({ children }) => (
        <pre className="my-2 p-3 rounded-none bg-stone-900 text-stone-100 text-xs font-mono overflow-x-auto border border-stone-800 [scrollbar-gutter:stable]">
          {children}
        </pre>
      ),
      code: ({ className, children, ...props }) => {
        const match = /language-(\w+)/.exec(className || "");
        const lang = match ? match[1].toLowerCase() : null;
        const codeText = String(children).replace(/\n$/, "");

        if (lang && Prism.languages[lang]) {
          try {
            const html = Prism.highlight(codeText, Prism.languages[lang], lang);
            return (
              <code
                className={`${className || ""} font-mono text-xs
                  [&_.token.property]:text-sky-400 [&_.token.string]:text-emerald-400
                  [&_.token.number]:text-amber-400 [&_.token.boolean]:text-purple-400
                  [&_.token.keyword]:text-rose-400 [&_.token.operator]:text-stone-400
                  [&_.token.comment]:text-stone-500 [&_.token.comment]:italic`}
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          } catch {
            // fallback to default
          }
        }

        const isBlock = match || String(children).includes("\n");
        if (isBlock) {
          return (
            <code className={`${className || ""} font-mono text-xs text-stone-200`} {...props}>
              {children}
            </code>
          );
        }

        return (
          <code
            className="font-mono text-xs px-1 py-0.5 rounded-none bg-stone-300/60 dark:bg-stone-800 text-amber-800 dark:text-amber-300"
            {...props}
          >
            {children}
          </code>
        );
      },
      mark: ({ node, ...props }: any) => {
        const isComposer = props["data-type"] === "composer";
        const commentId = props["data-comment-id"];
        const isHighlighted = props["data-highlighted"] === "true";

        if (isComposer) {
          return (
            <mark
              className="bg-amber-400 dark:bg-amber-400 text-stone-950 font-medium ring-2 ring-amber-500/90 rounded-none px-0.5 shadow-xs"
              title="Currently being commented on"
            >
              {props.children}
            </mark>
          );
        }

        return (
          <mark
            onClick={(e) => {
              e.stopPropagation();
              if (commentId) {
                onCommentClick(commentId);
              }
            }}
            className={`cursor-pointer rounded-none px-0.5 transition duration-150 ${
              isHighlighted
                ? "bg-amber-300 dark:bg-amber-500 text-stone-950 font-medium ring-2 ring-amber-400"
                : "bg-amber-200/60 dark:bg-amber-500/30 text-inherit hover:bg-amber-300/80 dark:hover:bg-amber-500/50"
            }`}
            title="Click to view operator comment"
          >
            {props.children}
          </mark>
        );
      },
    }),
    [onCommentClick]
  );

  return (
    <div
      id={`msg-text-${msgId}`}
      onMouseUp={onSelectText ? () => onSelectText(msgId) : undefined}
      className="leading-relaxed selection:bg-amber-200/80 dark:selection:bg-amber-900/60"
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeSnippetPlugin]}
        components={markdownComponents}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
